import React, { Dispatch, SetStateAction } from "react"
import tw, { styled } from "twin.macro"
import { Maybe, ActivityDetailFragment, ActivityType } from "../../graphql/generated/graphql"
import { Button } from "../formElements/button"
import Image from "next/image"
import { getEmojiForReaction } from "../emojiReactions"
import { ReactComponent as ArrowLeftPrimarySVG } from "../../assets/icons/arrow-left-primary.svg"
import { useTranslation } from "next-i18next"
import { MediaTranscodesLoader } from "../../utils/loader"
import ProfilePhoto, { Width } from "../profilePhoto"
import Link from "next/link"
import { Routes } from "../../routes"

const ArrowLeftPrimary = styled(ArrowLeftPrimarySVG)(() => [tw`h-4 w-4 absolute top-1`])

interface ActivityProps {
  activity: ActivityDetailFragment
  isFeatured?: boolean
}
export const Activity = ({ activity, isFeatured }: ActivityProps) => {
  const { type, Question, Actor, User, Answer, Reaction } = activity
  const id = Question?.id || Answer?.questionId || Reaction?.questionId
  const body = Question?.body
  const actor = Actor?.name
  const user = User?.name
  const verified = Actor?.verified
  const profilePictureUrl = Actor?.profilePictureUrl || ""
  const coverImageUrl = Answer?.coverImage
  const emojiKey = Reaction?.emoji || ""

  const renderActivity = () => {
    const { t } = useTranslation("activity")
    const altText = `${actor} answered your question`
    const answerButton = <Button action={`Answer`} twStyle={tw`px-4 py-1 text-xs`} />
    const coverImage = (
      <>
        {coverImageUrl && (
          <Image alt={altText} src={coverImageUrl} width={28} height={28} sizes="15vw" loader={MediaTranscodesLoader} />
        )}
      </>
    )
    const emoji = <div tw="text-xl w-7 h-7 flex justify-center items-center">{getEmojiForReaction(emojiKey)}</div>
    switch (type) {
      case ActivityType.QuestionCreated:
        const message = (
          <>
            <strong>{actor}</strong>
            {` ${t("question_created.action")}, "${body}"`}
          </>
        )
        const featuredMessage = (
          <>
            <strong>{actor}</strong>
            {` ${t("question_created.action")} `}
            <strong>{`${user}, `}</strong>
            {`"${body}"`}
          </>
        )
        return {
          message: isFeatured ? featuredMessage : message,
          media: isFeatured ? <></> : answerButton,
        }
      case ActivityType.QuestionExpiring:
        return {
          message: (
            <>
              <strong>{actor}’s</strong>
              {` ${t("question_expiring.object")} ${t("question_expiring.action")} "${body}"`}
            </>
          ),
          media: isFeatured ? <></> : answerButton,
        }
      case ActivityType.ReactionQuestion:
        return {
          message: (
            <>
              <strong>{actor}</strong>
              {` ${t("reaction_question.action")} `}
              <strong>{isFeatured ? `${user}'s` : "your"}</strong>
              {` ${t("reaction_question.object")} `}
            </>
          ),
          media: emoji,
        }
      case ActivityType.QuestionAnswered:
        return {
          message: (
            <>
              <strong>{actor}</strong>
              {` ${t("question_answered.action")} `}
              <strong>{isFeatured ? `${user}'s` : "your"}</strong>
              {` ${t("question_answered.object")} `}
            </>
          ),
          media: coverImage,
        }
      case ActivityType.AnswerRedeemed:
        return {
          message: (
            <>
              <strong>{actor}</strong>
              {` ${t("answer_redeemed.action")} `}
              <strong>{isFeatured ? `${user}'s` : "your"}</strong>
              {` ${t("answer_redeemed.object")} `}
            </>
          ),
          media: coverImage,
        }
      case ActivityType.ReactionAnswer:
        return {
          message: (
            <>
              <strong>{actor}</strong>
              {` ${t("reaction_answer.action")} `}
              <strong>{isFeatured ? `${user}'s` : "your"}</strong>
              {` ${t("reaction_answer.object")} `}
            </>
          ),
          media: emoji,
        }
      case ActivityType.NotifyRedeemCollectible:
        return {
          message: (
            <>
              {`${t("redeem_collectible")} `}
              <strong>{actor}</strong>
            </>
          ),
          media: coverImage,
        }
      default:
        return { message: <></>, media: <></> }
    }
  }
  return (
    <div tw="border-b-2 border-base-1 last:border-b-0">
      <Link href={{ pathname: Routes.ShowQuestion, query: { id } }} passHref>
        <a tw="py-2 flex justify-between items-center space-x-2.5 w-full hover:(no-underline)">
          <div tw="space-x-2.5 flex items-center">
            <ProfilePhoto profilePictureUrl={profilePictureUrl} width={Width.W28} showVerified={verified} />
            <p tw="text-base-4 text-xs overflow-hidden overflow-ellipsis whitespace-pre-wrap line-clamp-3">
              {renderActivity().message}
            </p>
          </div>
          {renderActivity().media}
        </a>
      </Link>
    </div>
  )
}

export interface ActivitiesProps {
  activities?: {
    newActivities: Maybe<ActivityDetailFragment>[]
    oldActivities: Maybe<ActivityDetailFragment>[]
  }
  setShowActivity: Dispatch<SetStateAction<boolean>>
}

export const Activities = ({ activities, setShowActivity }: ActivitiesProps) => {
  const newActivities = activities?.newActivities
  const oldActivities = activities?.oldActivities
  const { t } = useTranslation("notifications")

  return (
    <div
      data-testid="activities"
      tw="p-4 space-y-4 bg-background w-screen h-screen overflow-y-auto tablet-lg:(max-h-[550px] w-[336px] h-auto border-2 border-base-2 rounded-lg shadow-base-2)"
    >
      <div tw="relative flex">
        <span tw="block tablet-lg:hidden" onClick={() => setShowActivity(false)}>
          <ArrowLeftPrimary />
        </span>
        <h4 tw="text-center text-lg w-full">{t("header")}</h4>
      </div>
      {newActivities && newActivities.length > 0 ? (
        <div>
          <h5 tw="text-sm font-bold">{t("new_header")}</h5>
          <ul>
            {newActivities.map(
              (activity: Maybe<ActivityDetailFragment>, i: number) =>
                activity && <Activity key={i} {...{ activity }} />,
            )}
          </ul>
        </div>
      ) : (
        <p tw="text-center font-bold text-base-4 text-sm w-full">{t("no_notifications")}</p>
      )}
      {oldActivities && oldActivities.length > 0 && (
        <div>
          <h5 tw="text-sm font-bold">{t("previous_header")}</h5>
          <ul>
            {oldActivities.map(
              (activity: Maybe<ActivityDetailFragment>, i: number) =>
                activity && <Activity key={i} {...{ activity }} />,
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
